/**
 * EPKPage - Electronic Press Kit Page
 * Features: Artist overview, stats, credits, performance clips, PDF export
 * Public URL: /epk/:stageName
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Download, Share2, Play, Music, Users, TrendingUp, MapPin,
  ExternalLink, ChevronRight, Award, Eye, Flame, ThumbsDown,
  Guitar, Calendar, Globe, Instagram, Twitter, Youtube, Link as LinkIcon,
  FileText, Copy, Check, X, Sparkles
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AIBioGenerator from '../components/AIBioGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#12101A] rounded-2xl p-5 border border-white/5"
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <p className="text-2xl font-bold text-white mb-1">{value}</p>
    <p className="text-[#71717A] text-sm">{label}</p>
    {subtext && <p className="text-[#A1A1AA] text-xs mt-1">{subtext}</p>}
  </motion.div>
);

// Market Location Item
const MarketItem = ({ location, plays, percentage }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center">
      <MapPin className="w-5 h-5 text-white" />
    </div>
    <div className="flex-1">
      <p className="text-white font-medium">{location}</p>
      <p className="text-[#71717A] text-sm">{plays?.toLocaleString()} plays</p>
    </div>
    <div className="w-20">
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF]"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-[#71717A] text-xs text-right mt-1">{percentage}%</p>
    </div>
  </div>
);

// Track Row Component
const TrackRow = ({ track, rank }) => (
  <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors">
    <span className="text-[#71717A] text-sm font-medium w-6">{rank}</span>
    <div className="w-12 h-12 rounded-lg bg-white/10 overflow-hidden flex-shrink-0">
      {track.artwork ? (
        <img src={track.artwork} alt={track.track_title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Music className="w-5 h-5 text-[#71717A]" />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium truncate">{track.track_title}</p>
      <p className="text-[#71717A] text-sm">{track.genre || 'Unknown Genre'}</p>
    </div>
    <div className="flex items-center gap-4 text-[#71717A] text-sm">
      <span className="flex items-center gap-1">
        <Play className="w-4 h-4" />
        {track.play_count?.toLocaleString() || 0}
      </span>
      <span className="flex items-center gap-1 text-green-400">
        <Flame className="w-4 h-4" />
        {track.vote_counts?.issa_hit || 0}
      </span>
    </div>
  </div>
);

// Performance Clip Card
const ClipCard = ({ clip }) => {
  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'youtube': return Youtube;
      case 'instagram': return Instagram;
      case 'twitter': return Twitter;
      default: return LinkIcon;
    }
  };
  const PlatformIcon = getPlatformIcon(clip.platform);
  
  return (
    <a 
      href={clip.url} 
      target="_blank" 
      rel="noopener noreferrer"
      className="block p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center">
          <PlatformIcon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{clip.title}</p>
          <p className="text-[#71717A] text-sm capitalize">{clip.platform}</p>
        </div>
        <ExternalLink className="w-4 h-4 text-[#71717A]" />
      </div>
    </a>
  );
};

// Credit Tag Component
const CreditTag = ({ name }) => (
  <span className="px-3 py-1.5 bg-white/5 rounded-full text-sm text-white border border-white/10">
    {name}
  </span>
);

// Share Modal Component
const ShareModal = ({ isOpen, onClose, epkUrl, stageName }) => {
  const [copied, setCopied] = useState(false);
  const fullUrl = `${window.location.origin}${epkUrl}`;
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="w-full max-w-md bg-[#12101A] rounded-3xl border border-white/10 p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Share EPK</h3>
            <button onClick={onClose} className="text-[#71717A] hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <p className="text-[#A1A1AA] mb-4">
            Share {stageName}&apos;s Electronic Press Kit
          </p>
          
          <div className="flex items-center gap-2 p-3 bg-white/5 rounded-xl mb-4">
            <input
              type="text"
              value={fullUrl}
              readOnly
              className="flex-1 bg-transparent text-white text-sm outline-none"
            />
            <button
              onClick={handleCopy}
              className="p-2 rounded-lg bg-[#FF007F] hover:bg-[#FF007F]/80 transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-white" /> : <Copy className="w-4 h-4 text-white" />}
            </button>
          </div>
          
          <p className="text-[#71717A] text-sm text-center">
            {copied ? 'Link copied!' : 'Copy link to share'}
          </p>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Main EPK Page Component
const EPKPage = () => {
  const { stageName } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const epkRef = useRef(null);
  
  const [epkData, setEpkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showAIBioGenerator, setShowAIBioGenerator] = useState(false);
  
  // Fetch EPK data
  useEffect(() => {
    const fetchEPK = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(`${API_URL}/api/artists/epk/${stageName}`);
        setEpkData(response.data);
        
        // Check if current user is the owner
        if (isAuthenticated && user) {
          const token = localStorage.getItem('token');
          try {
            const profileRes = await axios.get(`${API_URL}/api/artists/profile/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (profileRes.data?.profile?.stage_name?.toLowerCase() === response.data?.artist?.stage_name?.toLowerCase()) {
              setIsOwner(true);
            }
          } catch {
            // Not logged in or no profile
          }
        }
      } catch (err) {
        console.error('Error fetching EPK:', err);
        setError(err.response?.status === 404 ? 'Artist not found' : 'Failed to load EPK');
      } finally {
        setLoading(false);
      }
    };
    
    if (stageName) {
      fetchEPK();
    }
  }, [stageName, isAuthenticated, user]);

  // Generate PDF EPK
  const generatePDF = useCallback(async () => {
    if (!epkData) return;
    
    setGeneratingPdf(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      let yPos = 20;
      
      // Colors
      const primaryColor = [255, 0, 127]; // #FF007F
      const accentColor = [0, 240, 255]; // #00F0FF
      
      // Header with gradient-like effect
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 50, 'F');
      
      // Artist Name
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.text(epkData.artist?.stage_name || 'Artist', margin, 35);
      
      // EPK Badge
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('ELECTRONIC PRESS KIT', pageWidth - margin, 20, { align: 'right' });
      pdf.text('NLT Radio', pageWidth - margin, 27, { align: 'right' });
      
      yPos = 65;
      
      // Bio Section
      if (epkData.artist?.bio) {
        pdf.setTextColor(50, 50, 50);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('ABOUT', margin, yPos);
        yPos += 8;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        const bioLines = pdf.splitTextToSize(epkData.artist.bio, pageWidth - 2 * margin);
        pdf.text(bioLines, margin, yPos);
        yPos += bioLines.length * 5 + 10;
      }
      
      // Stats Section
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(50, 50, 50);
      pdf.text('STATS', margin, yPos);
      yPos += 10;
      
      // Stats grid
      const stats = [
        ['Total Streams', epkData.stats?.total_streams?.toLocaleString() || '0'],
        ['Followers', epkData.stats?.followers?.toLocaleString() || '0'],
        ['Total Tracks', epkData.stats?.total_tracks?.toString() || '0'],
        ['Net Vote Score', (epkData.stats?.votes?.net_score >= 0 ? '+' : '') + (epkData.stats?.votes?.net_score || 0)]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [],
        body: stats,
        theme: 'plain',
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 50 },
          1: { textColor: [...primaryColor] }
        },
        margin: { left: margin }
      });
      
      yPos = pdf.lastAutoTable.finalY + 15;
      
      // Vote Breakdown
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('VOTE BREAKDOWN', margin, yPos);
      yPos += 10;
      
      const voteStats = [
        ['Issa Hit 🔥', epkData.stats?.votes?.issa_hit?.toString() || '0'],
        ['Flop 👎', epkData.stats?.votes?.flop?.toString() || '0'],
        ['Rocking With It 🎸', epkData.stats?.votes?.rocking?.toString() || '0']
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [],
        body: voteStats,
        theme: 'plain',
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: { 0: { cellWidth: 60 } },
        margin: { left: margin }
      });
      
      yPos = pdf.lastAutoTable.finalY + 15;
      
      // Top Markets
      if (epkData.top_markets?.length > 0) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('TOP MARKETS', margin, yPos);
        yPos += 10;
        
        const marketRows = epkData.top_markets.map(m => [m.location, `${m.plays} plays`, `${m.percentage}%`]);
        
        autoTable(pdf, {
          startY: yPos,
          head: [['Location', 'Plays', 'Share']],
          body: marketRows,
          theme: 'striped',
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [...primaryColor] },
          margin: { left: margin, right: margin }
        });
        
        yPos = pdf.lastAutoTable.finalY + 15;
      }
      
      // Check if we need a new page
      if (yPos > 230) {
        pdf.addPage();
        yPos = 20;
      }
      
      // Credits Section
      const credits = epkData.credits || {};
      const hasCredits = Object.values(credits).some(arr => arr?.length > 0);
      
      if (hasCredits) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('CREDITS', margin, yPos);
        yPos += 10;
        
        const creditTypes = [
          { key: 'songwriters', label: 'Songwriters' },
          { key: 'composers', label: 'Composers' },
          { key: 'producers', label: 'Producers' },
          { key: 'vocalists', label: 'Vocalists' }
        ];
        
        creditTypes.forEach(({ key, label }) => {
          if (credits[key]?.length > 0) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.text(`${label}:`, margin, yPos);
            pdf.setFont('helvetica', 'normal');
            pdf.text(credits[key].join(', '), margin + 25, yPos);
            yPos += 6;
          }
        });
        
        yPos += 10;
      }
      
      // Top Tracks
      if (epkData.tracks?.length > 0) {
        // Check for new page
        if (yPos > 200) {
          pdf.addPage();
          yPos = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('TOP TRACKS', margin, yPos);
        yPos += 10;
        
        const trackRows = epkData.tracks.slice(0, 5).map((t, i) => [
          `${i + 1}`,
          t.track_title,
          t.genre || 'Unknown',
          `${t.play_count?.toLocaleString() || 0} plays`,
          `+${t.vote_counts?.issa_hit || 0} hits`
        ]);
        
        autoTable(pdf, {
          startY: yPos,
          head: [['#', 'Track', 'Genre', 'Streams', 'Votes']],
          body: trackRows,
          theme: 'striped',
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [...primaryColor] },
          margin: { left: margin, right: margin }
        });
        
        yPos = pdf.lastAutoTable.finalY + 15;
      }
      
      // Contact / Social Links
      const socials = epkData.artist?.social_links || {};
      const hasSocials = Object.values(socials).some(v => v);
      
      if (hasSocials || epkData.artist?.location) {
        // Check for new page
        if (yPos > 240) {
          pdf.addPage();
          yPos = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('CONTACT & SOCIALS', margin, yPos);
        yPos += 10;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        
        if (epkData.artist?.location) {
          pdf.text(`Location: ${epkData.artist.location}`, margin, yPos);
          yPos += 5;
        }
        
        Object.entries(socials).forEach(([platform, link]) => {
          if (link) {
            pdf.text(`${platform.charAt(0).toUpperCase() + platform.slice(1)}: ${link}`, margin, yPos);
            yPos += 5;
          }
        });
      }
      
      // Footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `${epkData.artist?.stage_name} - NLT Radio EPK - Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pdf.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
        pdf.text(
          new Date().toLocaleDateString(),
          pageWidth - margin,
          pdf.internal.pageSize.getHeight() - 10,
          { align: 'right' }
        );
      }
      
      // Save PDF
      const fileName = `${epkData.artist?.stage_name?.replace(/\s+/g, '_') || 'artist'}_EPK.pdf`;
      pdf.save(fileName);
      
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  }, [epkData]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-2 border-[#FF007F] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-[#71717A]" />
          <h2 className="text-2xl font-bold text-white mb-2">{error}</h2>
          <p className="text-[#A1A1AA] mb-6">The EPK you&apos;re looking for doesn&apos;t exist.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const artist = epkData?.artist || {};
  const stats = epkData?.stats || {};
  const credits = epkData?.credits || {};

  return (
    <div className="min-h-screen bg-[#050505]" ref={epkRef}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-white hover:text-[#00F0FF] transition-colors"
            >
              <ChevronRight className="w-6 h-6 rotate-180" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
                Electronic Press Kit
              </h1>
              <p className="text-[#71717A] text-sm">{artist.stage_name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShareModal(true)}
              className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="epk-share-btn"
            >
              <Share2 className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={generatePDF}
              disabled={generatingPdf}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="epk-download-pdf-btn"
            >
              <Download className="w-4 h-4" />
              {generatingPdf ? 'Generating...' : 'Download PDF'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 pb-24">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-3xl overflow-hidden mb-8"
        >
          {/* Cover Photo */}
          <div className="h-48 md:h-64 bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/30">
            {artist.cover_photo && (
              <img src={artist.cover_photo} alt="Cover" className="w-full h-full object-cover" />
            )}
          </div>
          
          {/* Profile Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
            <div className="flex items-end gap-4">
              {/* Profile Picture */}
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden border-4 border-[#050505] bg-[#12101A] flex-shrink-0">
                {artist.profile_picture ? (
                  <img src={artist.profile_picture} alt={artist.stage_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Music className="w-12 h-12 text-[#71717A]" />
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
                    {artist.stage_name}
                  </h2>
                  {artist.verified && (
                    <Award className="w-6 h-6 text-[#00F0FF]" />
                  )}
                </div>
                
                {artist.genres?.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {artist.genres.map((genre, i) => (
                      <span key={i} className="px-2 py-1 bg-white/10 rounded-full text-xs text-white">
                        {genre}
                      </span>
                    ))}
                  </div>
                )}
                
                {artist.location && (
                  <p className="text-[#A1A1AA] text-sm mt-2 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {artist.location}
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bio Section */}
        {artist.bio ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#12101A] rounded-2xl p-6 border border-white/5 mb-6"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#FF007F]" />
                About
              </h3>
              {isOwner && (
                <button
                  onClick={() => setShowAIBioGenerator(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#FF007F]/20 to-[#00F0FF]/20 
                    border border-[#FF007F]/30 text-[#FF007F] text-sm font-medium hover:bg-[#FF007F]/30 transition-colors"
                  data-testid="ai-bio-edit-btn"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  AI Rewrite
                </button>
              )}
            </div>
            <p className="text-[#A1A1AA] leading-relaxed">{artist.bio}</p>
          </motion.div>
        ) : isOwner && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#12101A] rounded-2xl p-6 border border-dashed border-white/20 mb-6 text-center"
          >
            <Sparkles className="w-10 h-10 mx-auto mb-3 text-[#FF007F]" />
            <h3 className="text-white font-semibold mb-2">No Bio Yet</h3>
            <p className="text-[#71717A] text-sm mb-4">Let AI write a compelling bio for your EPK</p>
            <button
              onClick={() => setShowAIBioGenerator(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl 
                bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-medium
                hover:opacity-90 transition-opacity"
              data-testid="ai-bio-generate-btn"
            >
              <Sparkles className="w-4 h-4" />
              Generate Bio with AI
            </button>
          </motion.div>
        )}

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          <StatCard
            icon={Play}
            label="Total Streams"
            value={stats.total_streams?.toLocaleString() || '0'}
            color="bg-gradient-to-br from-[#FF007F] to-[#FF007F]/50"
          />
          <StatCard
            icon={Users}
            label="Followers"
            value={stats.followers?.toLocaleString() || '0'}
            color="bg-gradient-to-br from-[#00F0FF] to-[#00F0FF]/50"
          />
          <StatCard
            icon={Music}
            label="Tracks"
            value={stats.total_tracks?.toString() || '0'}
            color="bg-gradient-to-br from-purple-500 to-purple-500/50"
          />
          <StatCard
            icon={TrendingUp}
            label="Net Score"
            value={(stats.votes?.net_score >= 0 ? '+' : '') + (stats.votes?.net_score || 0)}
            color="bg-gradient-to-br from-green-500 to-green-500/50"
            subtext="Issa Hit - Flop"
          />
        </motion.div>

        {/* Vote Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-[#12101A] rounded-2xl p-6 border border-white/5 mb-6"
        >
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#00F0FF]" />
            Vote Breakdown
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-xl bg-white/5">
              <Flame className="w-8 h-8 mx-auto mb-2 text-orange-400" />
              <p className="text-2xl font-bold text-white">{stats.votes?.issa_hit || 0}</p>
              <p className="text-[#71717A] text-sm">Issa Hit</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-white/5">
              <ThumbsDown className="w-8 h-8 mx-auto mb-2 text-red-400" />
              <p className="text-2xl font-bold text-white">{stats.votes?.flop || 0}</p>
              <p className="text-[#71717A] text-sm">Flop</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-white/5">
              <Guitar className="w-8 h-8 mx-auto mb-2 text-cyan-400" />
              <p className="text-2xl font-bold text-white">{stats.votes?.rocking || 0}</p>
              <p className="text-[#71717A] text-sm">Rocking With It</p>
            </div>
          </div>
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Top Markets */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#12101A] rounded-2xl p-6 border border-white/5"
          >
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#FF007F]" />
              Top Markets
            </h3>
            {epkData?.top_markets?.length > 0 ? (
              <div className="space-y-3">
                {epkData.top_markets.map((market, i) => (
                  <MarketItem key={i} {...market} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#71717A]">
                <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Market data will appear as streams grow</p>
              </div>
            )}
          </motion.div>

          {/* Credits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-[#12101A] rounded-2xl p-6 border border-white/5"
          >
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#00F0FF]" />
              Credits
            </h3>
            {Object.values(credits).some(arr => arr?.length > 0) ? (
              <div className="space-y-4">
                {credits.songwriters?.length > 0 && (
                  <div>
                    <p className="text-[#71717A] text-sm mb-2">Songwriters</p>
                    <div className="flex flex-wrap gap-2">
                      {credits.songwriters.map((name, i) => (
                        <CreditTag key={i} name={name} />
                      ))}
                    </div>
                  </div>
                )}
                {credits.producers?.length > 0 && (
                  <div>
                    <p className="text-[#71717A] text-sm mb-2">Producers</p>
                    <div className="flex flex-wrap gap-2">
                      {credits.producers.map((name, i) => (
                        <CreditTag key={i} name={name} />
                      ))}
                    </div>
                  </div>
                )}
                {credits.composers?.length > 0 && (
                  <div>
                    <p className="text-[#71717A] text-sm mb-2">Composers</p>
                    <div className="flex flex-wrap gap-2">
                      {credits.composers.map((name, i) => (
                        <CreditTag key={i} name={name} />
                      ))}
                    </div>
                  </div>
                )}
                {credits.vocalists?.length > 0 && (
                  <div>
                    <p className="text-[#71717A] text-sm mb-2">Vocalists</p>
                    <div className="flex flex-wrap gap-2">
                      {credits.vocalists.map((name, i) => (
                        <CreditTag key={i} name={name} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-[#71717A]">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Credits pulled from track metadata</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Performance Clips */}
        {epkData?.performance_clips?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-[#12101A] rounded-2xl p-6 border border-white/5 mb-6"
          >
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-[#FF007F]" />
              Performance Clips
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {epkData.performance_clips.map((clip, i) => (
                <ClipCard key={i} clip={clip} />
              ))}
            </div>
          </motion.div>
        )}

        {/* Top Tracks */}
        {epkData?.tracks?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-[#12101A] rounded-2xl p-6 border border-white/5 mb-6"
          >
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#00F0FF]" />
              Top Tracks
            </h3>
            <div className="space-y-1">
              {epkData.tracks.map((track, i) => (
                <TrackRow key={track.id} track={track} rank={i + 1} />
              ))}
            </div>
          </motion.div>
        )}

        {/* Social Links */}
        {Object.values(artist.social_links || {}).some(v => v) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-[#12101A] rounded-2xl p-6 border border-white/5"
          >
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#FF007F]" />
              Connect
            </h3>
            <div className="flex flex-wrap gap-3">
              {artist.social_links?.instagram && (
                <a
                  href={artist.social_links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 transition-opacity"
                >
                  <Instagram className="w-4 h-4" />
                  Instagram
                </a>
              )}
              {artist.social_links?.twitter && (
                <a
                  href={artist.social_links.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1DA1F2] text-white hover:opacity-90 transition-opacity"
                >
                  <Twitter className="w-4 h-4" />
                  Twitter
                </a>
              )}
              {artist.social_links?.youtube && (
                <a
                  href={artist.social_links.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FF0000] text-white hover:opacity-90 transition-opacity"
                >
                  <Youtube className="w-4 h-4" />
                  YouTube
                </a>
              )}
              {artist.social_links?.website && (
                <a
                  href={artist.social_links.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  <LinkIcon className="w-4 h-4" />
                  Website
                </a>
              )}
            </div>
          </motion.div>
        )}

        {/* Owner Actions */}
        {isOwner && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="mt-6 p-4 rounded-2xl border border-[#FF007F]/30 bg-[#FF007F]/5"
          >
            <p className="text-[#FF007F] text-sm mb-3 font-medium">
              This is your EPK. You can edit your profile and add performance clips from your dashboard.
            </p>
            <Link
              to="/artist-dashboard"
              className="inline-flex items-center gap-2 text-white hover:text-[#00F0FF] transition-colors"
            >
              Go to Dashboard
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        )}
      </main>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        epkUrl={epkData?.epk_url || `/epk/${stageName}`}
        stageName={artist.stage_name}
      />

      {/* AI Bio Generator Modal */}
      <AnimatePresence>
        {showAIBioGenerator && (
          <AIBioGenerator
            currentBio={artist.bio}
            onBioSaved={(newBio) => {
              // Update local EPK data with new bio
              setEpkData(prev => ({
                ...prev,
                artist: { ...prev.artist, bio: newBio }
              }));
            }}
            onClose={() => setShowAIBioGenerator(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default EPKPage;
